import { Check } from "lucide-react"
import { useState, useEffect } from "react"

const backgrounds = [
  { 
    name: "Default", 
    value: "default",
    colors: {
      light: { background: "0 0% 100%", card: "0 0% 100%", muted: "210 40% 96.1%", border: "214.3 31.8% 91.4%" },
      dark: { background: "222.2 84% 4.9%", card: "222.2 84% 4.9%", muted: "217.2 32.6% 17.5%", border: "217.2 32.6% 17.5%" }
    }
  },
  { 
    name: "Slate", 
    value: "slate",
    colors: {
      light: { background: "0 0% 100%", card: "0 0% 100%", muted: "210 40% 96.1%", border: "214.3 31.8% 91.4%" },
      dark: { background: "222.2 47% 11%", card: "217.2 33% 17%", muted: "215 27% 25%", border: "215 20% 27%" }
    }
  },
  { 
    name: "Gray", 
    value: "gray",
    colors: {
      light: { background: "0 0% 100%", card: "0 0% 100%", muted: "0 0% 96.1%", border: "0 0% 89.8%" },
      dark: { background: "0 0% 9%", card: "0 0% 14.9%", muted: "0 0% 20%", border: "0 0% 22%" }
    }
  },
  { 
    name: "Zinc", 
    value: "zinc",
    colors: {
      light: { background: "0 0% 100%", card: "0 0% 100%", muted: "240 4.8% 95.9%", border: "240 5.9% 90%" },
      dark: { background: "240 10% 3.9%", card: "240 4% 10%", muted: "240 4% 16%", border: "240 4% 18%" }
    }
  },
  { 
    name: "Neutral", 
    value: "neutral",
    colors: {
      light: { background: "0 0% 100%", card: "0 0% 100%", muted: "0 0% 96.1%", border: "0 0% 89.8%" },
      dark: { background: "0 0% 10%", card: "0 0% 15%", muted: "0 0% 20%", border: "0 0% 23%" }
    }
  },
  { 
    name: "Stone", 
    value: "stone",
    colors: {
      light: { background: "0 0% 100%", card: "0 0% 100%", muted: "60 4.8% 95.9%", border: "20 5.9% 90%" },
      dark: { background: "20 14.3% 4.1%", card: "24 9.8% 10%", muted: "12 6.5% 15.1%", border: "12 6.5% 17%" }
    }
  },
]

export function ThemeBackgroundSelector() {
  const [currentBackground, setCurrentBackground] = useState("default")

  useEffect(() => {
    const saved = localStorage.getItem("theme-background") || "neutral"
    setCurrentBackground(saved)
    applyBackground(saved)

    // Ouvir mudanças no tema light/dark para reaplicar backgrounds
    const handleThemeColorChange = () => {
      const current = localStorage.getItem("theme-background") || "default"
      applyBackground(current)
    }

    window.addEventListener("theme-color-change", handleThemeColorChange as EventListener)
    
    return () => {
      window.removeEventListener("theme-color-change", handleThemeColorChange as EventListener)
    }
  }, [])

  const applyBackground = (bgTheme: string) => {
    // Don't override a non-custom preset's colors
    const activePreset = document.documentElement.getAttribute("data-preset") || "custom"
    if (activePreset !== "custom") return

    const root = document.documentElement
    const isDark = root.classList.contains('dark')
    
    const theme = backgrounds.find(b => b.value === bgTheme)
    if (!theme) return

    const colors = isDark ? theme.colors.dark : theme.colors.light

    root.style.setProperty("--background", colors.background)
    root.style.setProperty("--card", colors.card)
    root.style.setProperty("--card-foreground", isDark ? "210 40% 98%" : "222.2 84% 4.9%")
    root.style.setProperty("--muted", colors.muted)
    root.style.setProperty("--muted-foreground", isDark ? "215 20.2% 65.1%" : "215.4 16.3% 46.9%")
    root.style.setProperty("--border", colors.border)
    root.style.setProperty("--input", colors.border)
    
    // Aplicar as mesmas cores na sidebar
    root.style.setProperty("--sidebar-background", colors.card)
    root.style.setProperty("--sidebar-foreground", isDark ? "210 40% 98%" : "222.2 84% 4.9%")
    root.style.setProperty("--sidebar-accent", colors.muted)
    root.style.setProperty("--sidebar-accent-foreground", isDark ? "210 40% 98%" : "222.2 47.4% 11.2%")
    root.style.setProperty("--sidebar-border", colors.border)
  }

  const handleBackgroundChange = (bgTheme: string) => {
    setCurrentBackground(bgTheme)
    localStorage.setItem("theme-background", bgTheme)
    applyBackground(bgTheme)
  }

  return (
    <div className="flex flex-wrap gap-2 justify-center">
      {backgrounds.map((bg) => (
        <button
          key={bg.value}
          onClick={() => handleBackgroundChange(bg.value)}
          className={`relative w-12 h-10 rounded-md border-2 transition-all hover:scale-105 overflow-hidden ${
            currentBackground === bg.value
              ? 'border-primary shadow-lg ring-2 ring-primary/20'
              : 'border-border hover:border-primary/50'
          }`}
          title={bg.name}
        >
          <div className={`w-full h-full ${
            bg.value === 'default' ? 'bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800' :
            bg.value === 'slate' ? 'bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700' :
            bg.value === 'gray' ? 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700' :
            bg.value === 'zinc' ? 'bg-gradient-to-br from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-700' :
            bg.value === 'neutral' ? 'bg-gradient-to-br from-neutral-100 to-neutral-200 dark:from-neutral-800 dark:to-neutral-700' :
            'bg-gradient-to-br from-stone-100 to-stone-200 dark:from-stone-800 dark:to-stone-700'
          }`} />
          {currentBackground === bg.value && (
            <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
              <Check className="h-4 w-4 text-white drop-shadow-sm" />
            </div>
          )}
        </button>
      ))}
    </div>
  )
}
